import {
  Bot,
  Clock,
  GitBranch,
  Globe,
  Grid3X3,
  Hourglass,
  MessageSquare,
  Phone,
  PhoneForwarded,
  PhoneOff,
  Play,
  Send,
  Sparkles,
  User,
  UserCircle,
  Users,
  Voicemail,
  Zap,
  type LucideIcon,
} from 'lucide-react'

export interface NodeDefinition {
  type: string
  /** Clé i18n pour le label. */
  labelKey: string
  /** Clé i18n pour la description. */
  descKey: string
  icon: LucideIcon
  defaultConfig: Record<string, unknown>
  /** Encore lu dans les flux qui l'ont, mais plus proposé à l'ajout. */
  horsPalette?: boolean
}

export interface NodeCategory {
  id: string
  /** Clé i18n pour le label. */
  labelKey: string
  icon: LucideIcon
  color: string
  bgColor: string
  borderColor: string
  nodes: NodeDefinition[]
}

/**
 * Les 12 node types — alignés sur Next IVRFlowEditor.tsx createNodeCategories.
 * Catégories : control, conditions, interaction, routing, ai.
 */
export const NODE_CATEGORIES: NodeCategory[] = [
  {
    id: 'control',
    labelKey: 'manager.flows.editor.categories.control',
    icon: Zap,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    borderColor: 'border-slate-300',
    nodes: [
      {
        type: 'start',
        labelKey: 'manager.flows.editor.nodes.start',
        descKey: 'manager.flows.editor.nodesDesc.start',
        icon: Play,
        defaultConfig: {},
      },
      {
        type: 'hangup',
        labelKey: 'manager.flows.editor.nodes.hangup',
        descKey: 'manager.flows.editor.nodesDesc.hangup',
        icon: PhoneOff,
        defaultConfig: {},
      },
    ],
  },
  {
    id: 'conditions',
    labelKey: 'manager.flows.editor.categories.conditions',
    icon: GitBranch,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-300',
    nodes: [
      {
        type: 'time_check',
        labelKey: 'manager.flows.editor.nodes.timeCheck',
        descKey: 'manager.flows.editor.nodesDesc.timeCheck',
        icon: Clock,
        defaultConfig: { timezone: 'America/Montreal' },
      },
      {
        type: 'caller_condition',
        labelKey: 'manager.flows.editor.nodes.callerCondition',
        descKey: 'manager.flows.editor.nodesDesc.callerCondition',
        icon: User,
        defaultConfig: {
          condition_type: 'prefix',
          value: '',
          phone_list_id: null,
        },
      },
      {
        type: 'called_number_condition',
        labelKey: 'manager.flows.editor.nodes.calledNumberCondition',
        descKey: 'manager.flows.editor.nodesDesc.calledNumberCondition',
        icon: Phone,
        defaultConfig: { phone_list_id: null },
      },
    ],
  },
  {
    id: 'interaction',
    labelKey: 'manager.flows.editor.categories.interaction',
    icon: MessageSquare,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
    nodes: [
      {
        type: 'language_select',
        labelKey: 'manager.flows.editor.nodes.languageSelect',
        descKey: 'manager.flows.editor.nodesDesc.languageSelect',
        icon: Globe,
        defaultConfig: {
          languages: [
            {
              key: '1',
              voice: 'Google.fr-CA-Neural2-A',
              label: 'Français',
              prompt: 'Pour le français, appuyez sur 1.',
              prompt_mode: 'tts',
            },
            {
              key: '2',
              voice: 'Google.en-US-Neural2-F',
              label: 'English',
              prompt: 'For English, press 2.',
              prompt_mode: 'tts',
            },
          ],
          timeout: 20,
          max_attempts: 3,
        },
      },
      {
        type: 'message',
        labelKey: 'manager.flows.editor.nodes.message',
        descKey: 'manager.flows.editor.nodesDesc.message',
        icon: MessageSquare,
        defaultConfig: { text: '', voice: 'Google.fr-CA-Neural2-A' },
      },
      {
        // Nœud « traversant » : il agit puis passe au suivant, sans rien
        // jouer à l'appelant. Branché typiquement sur la sortie « sans
        // réponse » d'un routage, pour écrire à qui vient d'appeler dans le
        // vide.
        type: 'sms',
        labelKey: 'manager.flows.editor.nodes.sms',
        descKey: 'manager.flows.editor.nodesDesc.sms',
        icon: Send,
        defaultConfig: { text: '', to: 'caller', custom_number: '' },
      },
      {
        type: 'menu',
        labelKey: 'manager.flows.editor.nodes.menu',
        descKey: 'manager.flows.editor.nodesDesc.menu',
        icon: Grid3X3,
        defaultConfig: {
          message: '',
          voice: 'Google.fr-CA-Neural2-A',
          options: [
            { key: '1', label: 'Option 1' },
            { key: '2', label: 'Option 2' },
          ],
          timeout: 20,
          max_attempts: 3,
        },
      },
    ],
  },
  {
    id: 'routing',
    labelKey: 'manager.flows.editor.categories.routing',
    icon: PhoneForwarded,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-300',
    nodes: [
      {
        type: 'route_team',
        labelKey: 'manager.flows.editor.nodes.routeTeam',
        descKey: 'manager.flows.editor.nodesDesc.routeTeam',
        icon: Users,
        defaultConfig: {
          team_id: null,
          ring_strategy: 'PARALLEL',
          ring_timeout: 30,
          // Si personne ne répond : l'appelant attend en musique qu'un collègue
          // se libère (au plus max_wait), au lieu de la sortie immédiate.
          patienter: false,
          max_wait: 120,
          message: 'Tous nos conseillers sont occupés. Restez en ligne, on vous répond dans un instant.',
          audio_url: '',
          audio_filename: '',
        },
      },
      {
        // File d'attente : remplacée par l'option « Faire patienter » de
        // « Router vers équipe » ; gardée pour afficher les flux qui l'ont.
        type: 'queue',
        horsPalette: true,
        labelKey: 'manager.flows.editor.nodes.queue',
        descKey: 'manager.flows.editor.nodesDesc.queue',
        icon: Hourglass,
        defaultConfig: {
          team_id: 'all',
          max_wait: 120,
          ring_timeout: 30,
          message: 'Tous nos conseillers sont occupés. Restez en ligne, on vous répond dans un instant.',
          audio_url: '',
          audio_filename: '',
        },
      },
      {
        type: 'route_agent',
        labelKey: 'manager.flows.editor.nodes.routeAgent',
        descKey: 'manager.flows.editor.nodesDesc.routeAgent',
        icon: UserCircle,
        defaultConfig: { agent_id: null, ring_timeout: 40 },
      },
      {
        type: 'route_number',
        labelKey: 'manager.flows.editor.nodes.routeNumber',
        descKey: 'manager.flows.editor.nodesDesc.routeNumber',
        icon: PhoneForwarded,
        defaultConfig: { phone_number: '', caller_id: 'company' },
      },
      {
        type: 'voicemail',
        labelKey: 'manager.flows.editor.nodes.voicemail',
        descKey: 'manager.flows.editor.nodesDesc.voicemail',
        icon: Voicemail,
        defaultConfig: {
          greeting: 'Laissez-nous un message après le signal sonore.',
          voice: 'Google.fr-CA-Neural2-A',
          max_duration: 120,
          transcribe: true,
        },
      },
    ],
  },
  {
    id: 'ai',
    labelKey: 'manager.flows.editor.categories.ai',
    icon: Sparkles,
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-300',
    nodes: [
      {
        type: 'route_ai',
        labelKey: 'manager.flows.editor.nodes.routeAi',
        descKey: 'manager.flows.editor.nodesDesc.routeAi',
        icon: Bot,
        defaultConfig: { ai_agent_id: null },
      },
    ],
  },
]

/** Trouve la catégorie d'un node_type. */
export function getCategoryByNodeType(
  nodeType: string,
): NodeCategory | undefined {
  return NODE_CATEGORIES.find((cat) =>
    cat.nodes.some((n) => n.type === nodeType),
  )
}

/** Trouve la définition d'un node_type. */
export function getNodeDefinition(
  nodeType: string,
): NodeDefinition | undefined {
  for (const cat of NODE_CATEGORIES) {
    const node = cat.nodes.find((n) => n.type === nodeType)
    if (node) return node
  }
  return undefined
}

/** Outputs (handles source) d'un node selon son type + config. */
export interface OutputHandle {
  id: string
  /** Clé i18n ou label brut (pour menu options). */
  label: string
}

export function getOutputHandles(
  nodeType: string,
  config: Record<string, unknown> | undefined,
  t: (key: string) => string,
): OutputHandle[] {
  switch (nodeType) {
    case 'start':
      return [{ id: 'default', label: '' }]
    case 'hangup':
      return []
    case 'time_check':
      return [
        { id: 'open', label: t('manager.flows.editor.outputs.open') },
        { id: 'closed', label: t('manager.flows.editor.outputs.closed') },
      ]
    case 'caller_condition':
    case 'called_number_condition':
      return [
        { id: 'match', label: t('manager.flows.editor.outputs.match') },
        { id: 'no_match', label: t('manager.flows.editor.outputs.noMatch') },
      ]
    case 'language_select': {
      const languages =
        (config?.languages as Array<{ key: string; label: string }>) || []
      const handles: OutputHandle[] = languages.map((lang) => ({
        id: lang.key,
        label: lang.label,
      }))
      handles.push({
        id: 'timeout',
        label: t('manager.flows.editor.outputs.timeout'),
      })
      handles.push({
        id: 'invalid',
        label: t('manager.flows.editor.outputs.invalid'),
      })
      return handles
    }
    case 'menu': {
      const options =
        (config?.options as Array<{ key: string; label: string }>) || []
      const handles: OutputHandle[] = options.map((opt) => ({
        id: opt.key,
        label: opt.label,
      }))
      handles.push({
        id: 'timeout',
        label: t('manager.flows.editor.outputs.timeout'),
      })
      handles.push({
        id: 'invalid',
        label: t('manager.flows.editor.outputs.invalid'),
      })
      return handles
    }
    case 'route_team':
      return [
        { id: 'answered', label: t('manager.flows.editor.outputs.answered') },
        {
          id: 'no_answer',
          label: config?.patienter
            ? t('manager.flows.editor.outputs.waitExpired')
            : t('manager.flows.editor.outputs.noAnswer'),
        },
      ]
    case 'route_agent':
    case 'route_number':
      return [
        { id: 'answered', label: t('manager.flows.editor.outputs.answered') },
        { id: 'no_answer', label: t('manager.flows.editor.outputs.noAnswer') },
      ]
    case 'queue':
      return [
        { id: 'answered', label: t('manager.flows.editor.outputs.answered') },
        { id: 'timeout', label: t('manager.flows.editor.outputs.waitExpired') },
      ]
    case 'route_ai':
      return [
        { id: 'completed', label: t('manager.flows.editor.outputs.completed') },
        {
          id: 'transfer',
          label: t('manager.flows.editor.outputs.transferRequested'),
        },
      ]
    case 'voicemail':
      return [
        {
          id: 'completed',
          label: t('manager.flows.editor.outputs.messageLeft'),
        },
      ]
    case 'message':
    case 'sms':
      return [{ id: 'next', label: '' }]
    default:
      return [{ id: 'default', label: '' }]
  }
}

/**
 * La configuration d'un nœud qu'on ajoute : voix et texte par défaut dans la langue
 * principale de l'entreprise. Changer la voix ensuite ne réécrit jamais le texte.
 */
export function configParDefaut(nodeDef: NodeDefinition, langue: 'fr' | 'en'): Record<string, unknown> {
  const config: Record<string, unknown> = { ...nodeDef.defaultConfig }
  if (langue !== 'en') return config
  if ('voice' in config) config.voice = 'Google.en-US-Neural2-C'
  if (nodeDef.type === 'voicemail') config.greeting = 'Please leave us a message after the tone.'
  if (nodeDef.type === 'queue' || nodeDef.type === 'route_team') config.message = 'All our agents are busy. Please stay on the line, we will be right with you.'
  return config
}
